import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Check, Folder, FolderOpen, Lock, Mail, User } from 'lucide-react';
import { Modal, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { saveTemplateApi, type EmailTemplateFolder } from '../useData';
import { recipientFieldsForPersistence } from '../utils/recipientUtils';
import type { ComposeBodyDocument } from '../composeBody/types';

interface SaveAsTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  canSaveCompany: boolean;
  personalFolders: EmailTemplateFolder[];
  companyFolders: EmailTemplateFolder[];
  /** Read live editor body at save time (preferred over stale formData.html). */
  getTemplateBodyForSave?: () => {
    html?: string;
    text?: string;
    bodyKind?: 'plain' | 'rich';
    composeState?: ComposeBodyDocument;
  };
  /** Read live recipient fields at save time (respects hidden Cc/Bcc rows). */
  getRecipientsForSave?: () => { to: string; cc: string; bcc: string };
  formData: {
    accountId?: number;
    to?: string;
    cc?: string;
    bcc?: string;
    subject?: string;
    html?: string;
  };
  onSaved: () => void;
}

type FolderTreeNode = EmailTemplateFolder & { children: FolderTreeNode[] };

const ChevronDown = (
  <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronRight = (
  <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

function isFolderPathLocked(folderId: number, folders: EmailTemplateFolder[]): boolean {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  let current: number | null = folderId;
  while (current != null) {
    const folder = byId.get(current);
    if (!folder) break;
    if (folder.isLocked) return true;
    current = folder.parentId;
  }
  return false;
}

function buildFolderTree(folders: EmailTemplateFolder[]): FolderTreeNode[] {
  const selectable = folders.filter((folder) => !folder.isSystemRoot);
  const byId = new Map<number, FolderTreeNode>();
  for (const folder of selectable) {
    byId.set(folder.id, { ...folder, children: [] });
  }
  const roots: FolderTreeNode[] = [];
  for (const node of Array.from(byId.values())) {
    if (node.parentId != null && byId.has(node.parentId)) {
      byId.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortNodes = (nodes: FolderTreeNode[]) => {
    nodes.sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
    nodes.forEach((node) => sortNodes(node.children));
  };
  sortNodes(roots);
  return roots;
}

function collectAncestorIds(folderId: number, folders: EmailTemplateFolder[]): number[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const ids: number[] = [];
  let current = byId.get(folderId);
  while (current?.parentId != null) {
    if (!byId.has(current.parentId) || byId.get(current.parentId)?.isSystemRoot) break;
    ids.push(current.parentId);
    current = byId.get(current.parentId);
  }
  return ids;
}

function findFolder(folders: EmailTemplateFolder[], id: number): EmailTemplateFolder | undefined {
  return folders.find((folder) => folder.id === id);
}

function FolderTreeRow({
  node,
  depth,
  selectedId,
  expandedIds,
  allFolders,
  onToggleExpand,
  onSelect,
  colors,
}: {
  node: FolderTreeNode;
  depth: number;
  selectedId: number | '';
  expandedIds: Set<number>;
  allFolders: EmailTemplateFolder[];
  onToggleExpand: (id: number) => void;
  onSelect: (id: number) => void;
  colors: ReturnType<typeof useTheme>['colors'];
}) {
  const hasChildren = node.children.length > 0;
  const expanded = expandedIds.has(node.id);
  const locked = isFolderPathLocked(node.id, allFolders);
  const selected = selectedId === node.id && !locked;
  const indentPl = 8 + depth * 12;

  return (
    <div role="treeitem" aria-expanded={hasChildren ? expanded : undefined} aria-selected={selected}>
      <div
        className={cn(
          'flex w-full min-w-0 items-center rounded-md border-l-2 transition-colors',
          selected
            ? cn(colors.primary, colors.primaryText, 'border-l-indigo-500 font-medium')
            : cn('border-transparent', colors.text, !locked && colors.interactiveHover),
          locked && 'cursor-not-allowed opacity-50',
        )}
        style={{ paddingLeft: indentPl }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(node.id);
            }}
            className={cn(
              'flex h-7 w-5 flex-shrink-0 items-center justify-center rounded p-0.5',
              selected ? 'hover:bg-white/15' : colors.interactiveHover,
            )}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? ChevronDown : ChevronRight}
          </button>
        ) : (
          <span className="inline-block h-7 w-5 flex-shrink-0" aria-hidden />
        )}
        <button
          type="button"
          disabled={locked}
          onClick={() => onSelect(node.id)}
          title={locked ? 'Folder is locked' : node.name}
          className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden border-0 bg-transparent py-1.5 pr-2 text-left text-sm text-inherit"
        >
          {selected ? (
            <FolderOpen className="h-4 w-4 flex-shrink-0" aria-hidden />
          ) : (
            <Folder className="h-4 w-4 flex-shrink-0 opacity-70" aria-hidden />
          )}
          <span className="min-w-0 flex-1 truncate">{node.name.trim() || 'Untitled folder'}</span>
          {locked ? (
            <Lock className="h-3.5 w-3.5 flex-shrink-0 opacity-70" aria-hidden />
          ) : selected ? (
            <Check className="h-3.5 w-3.5 flex-shrink-0 opacity-90" aria-hidden />
          ) : null}
        </button>
      </div>
      {hasChildren && expanded ? (
        <div role="group">
          {node.children.map((child) => (
            <FolderTreeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              expandedIds={expandedIds}
              allFolders={allFolders}
              onToggleExpand={onToggleExpand}
              onSelect={onSelect}
              colors={colors}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

const SaveAsTemplateModal: React.FC<SaveAsTemplateModalProps> = ({
  isOpen,
  onClose,
  canSaveCompany,
  personalFolders,
  companyFolders,
  getTemplateBodyForSave,
  getRecipientsForSave,
  formData,
  onSaved,
}) => {
  const { colors } = useTheme();
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'PERSONAL' | 'COMPANY'>(canSaveCompany ? 'COMPANY' : 'PERSONAL');
  const [folderId, setFolderId] = useState<number | ''>('');
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeFolders = scope === 'COMPANY' ? companyFolders : personalFolders;
  const folderTree = useMemo(() => buildFolderTree(activeFolders), [activeFolders]);
  const selectableFolders = useMemo(
    () => activeFolders.filter((folder) => !folder.isSystemRoot),
    [activeFolders],
  );

  const selectedFolder = folderId === '' ? undefined : findFolder(activeFolders, folderId);
  const selectedLocked =
    folderId !== '' && selectedFolder != null && isFolderPathLocked(folderId, activeFolders);

  const toggleExpand = useCallback((id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectFolder = useCallback(
    (id: number) => {
      if (isFolderPathLocked(id, activeFolders)) return;
      setFolderId(id);
      setError(null);
      setExpandedIds((prev) => {
        const next = new Set(prev);
        for (const ancestorId of collectAncestorIds(id, activeFolders)) {
          next.add(ancestorId);
        }
        return next;
      });
    },
    [activeFolders],
  );

  useEffect(() => {
    if (!isOpen) return;
    const nextScope = canSaveCompany ? 'COMPANY' : 'PERSONAL';
    const folders = nextScope === 'COMPANY' ? companyFolders : personalFolders;
    const tree = buildFolderTree(folders);
    const unlocked = folders.filter(
      (folder) => !folder.isSystemRoot && !isFolderPathLocked(folder.id, folders),
    );

    setName(formData.subject?.trim() || '');
    setScope(nextScope);
    setFolderId(unlocked.length === 1 ? unlocked[0].id : '');
    setExpandedIds(new Set(tree.filter((node) => node.children.length > 0).map((node) => node.id)));
    setError(null);
    // Intentionally reset only when the modal opens (and default scope capability / subject seed).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid resetting selection on folder refetch
  }, [isOpen, formData.subject, canSaveCompany]);

  useEffect(() => {
    if (!isOpen) return;
    const unlocked = selectableFolders.filter((folder) => !isFolderPathLocked(folder.id, activeFolders));
    setFolderId(unlocked.length === 1 ? unlocked[0].id : '');
    setExpandedIds(new Set(folderTree.filter((node) => node.children.length > 0).map((node) => node.id)));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset picker only when library scope changes
  }, [scope]);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (folderId === '' || selectedLocked) {
      setError('Select a folder for the template');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const savedBody = getTemplateBodyForSave?.();
      const html = savedBody?.html ?? formData.html;
      const recipients = getRecipientsForSave?.() ?? recipientFieldsForPersistence(formData);
      await saveTemplateApi({
        name: trimmed,
        scope,
        folderId,
        accountId: formData.accountId,
        ...recipients,
        subject: formData.subject,
        html,
        text: savedBody?.text,
        bodyKind: savedBody?.bodyKind,
        composeState: savedBody?.composeState,
      });
      onSaved();
      onClose();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const canSubmit =
    !saving && name.trim().length > 0 && folderId !== '' && !selectedLocked && selectableFolders.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" closeOnEscape={!saving}>
      <div
        className={cn(
          'flex shrink-0 items-start gap-4 border-b px-6 py-5',
          colors.border,
          'bg-gradient-to-r from-indigo-50/90 via-white to-violet-50/50',
          'dark:from-indigo-950/30 dark:via-gray-950 dark:to-violet-950/20',
        )}
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm">
          <Mail className="h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className={cn('text-lg font-semibold', colors.text)}>Save as template</h2>
          <p className={cn('mt-1 text-sm', colors.textMuted)}>
            Name the template and choose where it lives in the library.
          </p>
        </div>
        <button
          type="button"
          onClick={handleClose}
          disabled={saving}
          className={cn('rounded-lg p-1.5', colors.textMuted, colors.interactiveHover, saving && 'opacity-50')}
          aria-label="Close"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
        <div>
          <label className={cn('mb-1.5 block text-sm font-medium', colors.text)} htmlFor="save-template-name">
            Template name
          </label>
          <input
            id="save-template-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={cn('w-full rounded-lg border px-3 py-2.5 text-sm', colors.border, colors.surface, colors.text)}
            placeholder="e.g. Weekly status update"
            autoFocus
          />
        </div>

        <div>
          <p className={cn('mb-1.5 text-sm font-medium', colors.text)}>Library</p>
          <div className={cn('grid gap-2', canSaveCompany ? 'grid-cols-2' : 'grid-cols-1')}>
            {canSaveCompany ? (
              <button
                type="button"
                onClick={() => setScope('COMPANY')}
                className={cn(
                  'flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-all',
                  scope === 'COMPANY'
                    ? 'border-indigo-300 bg-indigo-50 shadow-sm dark:border-indigo-800 dark:bg-indigo-950/40'
                    : cn(colors.border, colors.surface, colors.interactiveHover),
                )}
              >
                <span
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-lg',
                    scope === 'COMPANY'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-indigo-100 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-300',
                  )}
                >
                  <Building2 className="h-4 w-4" aria-hidden />
                </span>
                <span className="min-w-0">
                  <span className={cn('block text-sm font-semibold', colors.text)}>Company</span>
                  <span className={cn('block text-[11px]', colors.textMuted)}>Visible to everyone</span>
                </span>
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setScope('PERSONAL')}
              className={cn(
                'flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-all',
                scope === 'PERSONAL'
                  ? 'border-emerald-300 bg-emerald-50 shadow-sm dark:border-emerald-800 dark:bg-emerald-950/40'
                  : cn(colors.border, colors.surface, colors.interactiveHover),
              )}
            >
              <span
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-lg',
                  scope === 'PERSONAL'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-300',
                )}
              >
                <User className="h-4 w-4" aria-hidden />
              </span>
              <span className="min-w-0">
                <span className={cn('block text-sm font-semibold', colors.text)}>Personal</span>
                <span className={cn('block text-[11px]', colors.textMuted)}>Private to you</span>
              </span>
            </button>
          </div>
          <p className={cn('mt-1.5 text-xs', colors.textMuted)}>
            {scope === 'COMPANY'
              ? 'Only company library editors can change it later.'
              : 'You can share folders with teammates if needed.'}
          </p>
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <label className={cn('text-sm font-medium', colors.text)} id="save-template-folder-label">
              Folder
            </label>
            <span className={cn('text-[11px] font-medium uppercase tracking-wider', colors.textMuted)}>
              Required
            </span>
          </div>
          <div
            className={cn(
              'overflow-hidden rounded-xl border',
              colors.border,
              'bg-gray-50/60 dark:bg-gray-900/30',
            )}
          >
            <div
              className={cn('max-h-56 min-h-[10rem] overflow-y-auto py-1 pl-1 pr-1 scrollbar-thin')}
              role="tree"
              aria-labelledby="save-template-folder-label"
            >
              {folderTree.length === 0 ? (
                <p className={cn('px-3 py-10 text-center text-sm', colors.textMuted)}>
                  {scope === 'PERSONAL'
                    ? 'Create a personal folder under Library before saving.'
                    : 'Create a company folder under Library before saving.'}
                </p>
              ) : (
                folderTree.map((node) => (
                  <FolderTreeRow
                    key={node.id}
                    node={node}
                    depth={0}
                    selectedId={folderId}
                    expandedIds={expandedIds}
                    allFolders={activeFolders}
                    onToggleExpand={toggleExpand}
                    onSelect={selectFolder}
                    colors={colors}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </div>
        ) : null}
      </div>

      <ModalFooter className="!justify-between gap-4">
        <div className={cn('min-w-0 text-xs', colors.textMuted)}>
          {selectedFolder && !selectedLocked ? (
            <>
              <span className="font-medium">Saving to</span>
              <span className="mx-1.5">→</span>
              <span className={cn('truncate font-medium', colors.text)}>{selectedFolder.name}</span>
            </>
          ) : (
            <span>Select a folder to continue</span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="secondary" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="default" onClick={() => void handleSubmit()} disabled={!canSubmit} className="min-w-[8rem]">
            {saving ? 'Saving…' : 'Save template'}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default SaveAsTemplateModal;
